<template>
  <div class="el-input">
    <input
      class="el-input__inner"
      v-bind="$attrs"
      ref="autocomplete"
      id="autocomplete"
      v-model="inputValue" />
  </div>
</template>

<script>
import gmapsInit from '@/utils/helpers/gmaps';
import format from '@/utils/helpers/format';

export default {
  name: 'inputAddress',
  inheritAttrs: false,
  props: {
    value: {
      type: String,
    },
    address: {
      type: Object,
      default: () => ({}),
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      shadow: '',
    };
  },
  computed: {
    componentForm() {
      return {
        street_number: 'short_name',
        route: 'long_name',
        locality: 'long_name',
        administrative_area_level_1: 'short_name',
        administrative_area_level_2: 'short_name',
        country: 'short_name',
        postal_code: 'short_name',
      };
    },
    inputValue: {
      get() {
        return this.value;
      },
      set(value) {
        if (value !== this.shadow) {
          this.shadow = '';
          this.$emit('update:address', {});
        }
        this.$emit('input', value);
      },
    },
  },
  async mounted() {
    if (this.autofocus) {
      this.$refs.autocomplete.focus();
    }
    const google = await gmapsInit();
    this.google = google;
    this.autocomplete = new google.maps.places.Autocomplete(
      this.$refs.autocomplete, {
        types: ['geocode'],
      },
    );
    this.autocomplete.setComponentRestrictions(
      // country list for autocomplete
      { country: ['fr'] },
    );
    this.autocomplete.addListener('place_changed', this.fillInAddress);
    if (this.$refs.autocomplete) {
      this.$refs.autocomplete.addEventListener('keypress', this.preventEnter);
    }
  },
  methods: {
    fillInAddress() {
      const place = this.autocomplete.getPlace();
      const address = {};
      if (place.address_components) {
        for (let i = 0; i < place.address_components.length; i += 1) {
          const [addressType] = place.address_components[i].types;
          if (this.componentForm[addressType]) {
            const val = place.address_components[i][
              this.componentForm[addressType]
            ];
            address[addressType] = val;
          }
        }
      }

      this.$nextTick(() => {
        this.$emit('input', place.formatted_address);
        this.shadow = place.formatted_address;
        this.$emit('update:address', this.formatInput(address));
      });
    },
    preventEnter(e) {
      // trick to prevent only when selecting an address
      const container = document.querySelector('.pac-container.pac-logo');
      if (e.keyCode === 13 && container && container.length) {
        e.preventDefault();
      }
    },
    getStreet(address) {
      let street = '';
      street += address.street_number || '';
      street += ' ';
      street += address.route || '';
      return street.trim() || '';
    },
    getPostal(address) {
      if (address.postal_code) return address.postal_code;
      return address.administrative_area_level_2
        ? this.getDep(address.administrative_area_level_2) : '';
    },
    formatInput(address) {
      return {
        street: this.getStreet(address),
        streetNumber: address.street_number,
        city: address.locality,
        region: address.administrative_area_level_1,
        country: address.country,
        postal: this.getPostal(address),
      };
    },
    getDep(departement) {
      const departements = {
        'Arrondissement de Paris': '75',
        'Departement de Paris': '75',
        Aisne: '02',
        Allier: '03',
        'Alpes-de-Haute-Provence': '04',
        'Hautes-Alpes': '05',
        'Alpes-Maritimes': '06',
        Ardeche: '07',
        Ardennes: '08',
        Ariege: '09',
        Aube: '10',
        Aude: '11',
        Aveyron: '12',
        'Bouches-du-Rhone': '13',
        Calvados: '14',
        Cantal: '15',
        Charente: '16',
        'Charente-Maritime': '17',
        Cher: '18',
        Correze: '19',
        Creuse: '23',
        Dordogne: '24',
        Doubs: '25',
        Drome: '26',
        Eure: '27',
        'Eure-et-Loir': '28',
        Finistere: '29',
        Gard: '30',
        'Haute-Garonne': '31',
        Gers: '32',
        Gironde: '33',
        Herault: '34',
        'Ille-et-Vilaine': '35',
        Indre: '36',
        'Indre-et-Loire': '37',
        Isere: '38',
        Jura: '39',
        Landes: '40',
        'Loir-et-Cher': '41',
        Loire: '42',
        'Haute-Loire': '43',
        'Loire-Atlantique': '44',
        Loiret: '45',
        Lot: '46',
        'Lot-et-Garonne': '47',
        Lozere: '48',
        'Maine-et-Loire': '49',
        Manche: '50',
        Marne: '51',
        'Haute-Marne': '52',
        Mayenne: '53',
        'Meurthe-et-Moselle': '54',
        Meuse: '55',
        Morbihan: '56',
        Moselle: '57',
        Nievre: '58',
        Nord: '59',
        Oise: '60',
        Orne: '61',
        'Pas-de-Calais': '62',
        'Puy-de-Dome': '63',
        'Pyrenees-Atlantiques': '64',
        'Hautes-Pyrenees': '65',
        'Pyrenees-Orientales': '66',
        'Bas-Rhin': '67',
        'Haut-Rhin': '68',
        Rhone: '69',
        'Haute-Saone': '70',
        'Saone-et-Loire': '71',
        Sarthe: '72',
        Savoie: '73',
        'Haute-Savoie': '74',
        Paris: '75',
        'Seine-Maritime': '76',
        'Seine-et-Marne': '77',
        Yvelines: '78',
        'Deux-Sevres': '79',
        Somme: '80',
        Tarn: '81',
        'Tarn-et-Garonne': '82',
        Var: '83',
        Vaucluse: '84',
        Vendee: '85',
        Vienne: '86',
        'Haute-Vienne': '87',
        Vosges: '88',
        Yonne: '89',
        'Territoire de Belfort': '90',
        Essonne: '91',
        'Hauts-de-Seine': '92',
        'Seine-Saint-Denis': '93',
        'Val-de-Marne': '94',
        'Val-d\'Oise': '95',
        'Cote-d\'Or': '21',
        'Cotes-d\'Armor': '22',
        'Corse-du-Sud': '20',
        'Haute-Corse': '20',
      };
      departement = format.removeAccents(departement);
      const postal = departements[departement];
      if (!postal && departement.match(/Paris/i)) {
        return '75000';
      }
      return postal ? `${postal}000` : '';
    },
    beforeDestroy() {
      this.$refs.autocomplete.removeEventListener('keypress', this.preventEnter);
    },
  },
};
</script>
